import { calculateLength } from '~/helpers/unitHelpers';
import type { CanvasEvent, HoverEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import { fontSize, padding, shouldConvertToMiles } from './common';

export default class DrawLineObject extends CanvasObject {
  vertices: VertexObject[] = [];

  pointerVertex: VertexObject;
  onLine: (o: LngLat[]) => void;

  constructor(id: string, surface: SurfaceBase, onLine: (p: LngLat[]) => void) {
    super(id, surface);
    this.pointerVertex = new VertexObject('pointer', this.surface, {
      point: { x: NaN, y: NaN },
      lngLat: { lng: NaN, lat: NaN },
    });
    this.addChild(this.pointerVertex);
    this.onLine = onLine;
  }

  get length() {
    const p = this.pointerVertex.lngLat;
    const store = this.getStore();
    const { currentUnitSystem: unitSystem } = store;

    const geometry: GeoJSON.LineString = {
      type: 'LineString',
      coordinates: [...this.vertices.map((v) => v.getLngLatAsArray()), [p.lng, p.lat]],
    };

    let l = calculateLength({ unitSystem, geometry });

    if (shouldConvertToMiles({ store, line: l })) {
      l = calculateLength({ unitSystem, unitType: 'miles', geometry });
    }

    return l;
  }

  onSurfaceTap = (e: CanvasEvent) => {
    const v = new VertexObject(`v-${this.vertices.length}`, this.surface, {
      point: e.point,
      lngLat: e.lngLat,
    });

    // if the mouse event is a double tap and the
    if (e.type === 'dblTap' && this.vertices.length >= 2) {
      this.onLine([...this.vertices.map((v) => v.lngLat)]);
    }

    this.vertices.push(v);
    this.surface.addObject(v);
  };

  onPointerMove = (e: HoverEvent) => {
    this.pointerVertex.setCoords(e);
    this.surface.renderAll();
  };

  calculateTextPoint = (textWidth: number): Point => {
    const offset = 25;
    const lastPoint = this.vertices[this.vertices.length - 1].point;
    const cursorPoint = this.pointerVertex.point;

    if (cursorPoint.x >= lastPoint.x) {
      return { x: cursorPoint.x + offset, y: cursorPoint.y };
    } else {
      return { x: cursorPoint.x - textWidth - offset, y: cursorPoint.y };
    }
  };

  render(ctx: CanvasRenderingContext2D) {
    if (this.vertices.length === 0) return;
    // Note: vertices are rendered by the object

    // lines
    function drawLine(p1: VertexObject, p2: VertexObject, dashed: boolean = false) {
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'red';
      dashed && ctx.setLineDash([10, 4]);
      ctx.moveTo(p1.point.x, p1.point.y);
      ctx.lineTo(p2.point.x, p2.point.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    for (let i = 0; i < this.vertices.length - 1; i++) {
      drawLine(this.vertices[i], this.vertices[i + 1]);
    }

    // draw last dashed line
    drawLine(this.vertices[this.vertices.length - 1], this.pointerVertex, true);

    // render length text
    const l = this.length;

    ctx.font = `bold ${fontSize}px arial`;
    const lengthText = `${l.value.toLocaleString()} ${l.unit}`;
    const textWidth = ctx.measureText(lengthText).width;
    const textPoint = this.calculateTextPoint(textWidth);
    ctx.fillStyle = 'red';
    ctx.beginPath();
    ctx.roundRect(textPoint.x, textPoint.y, textWidth + padding, fontSize + padding, [5]);
    ctx.fill();
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'white';
    ctx.fillText(lengthText, textPoint.x + padding / 2, textPoint.y + padding / 2);
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    // no hit area needed for drawing a line.
  }

  onKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && this.vertices.length >= 2) {
      return this.onLine([...this.vertices.map((v) => v.lngLat)]);
    }
  };
}
